import { useEffect } from 'react';

import { useParams } from 'react-router';

import { IconCertificate, IconPrinter } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { IconButton } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import { useCategoryLabelsGrouped } from '@/hooks/useCategoryLabels';
import { useFlags } from '@/hooks/useFlags';
import useGetXPSummaryReportByUser from '@/hooks/useGetXPSummaryReportByUser';
import { useScrollMemory } from '@/hooks/useScrollMemory/useScrollMemory';
import { useSortOptions } from '@/hooks/useSortOptions';

import { getFilterableStatus } from '@/utils/getFilterableStatus';
import { itemByLanguage } from '@/utils/language';
import {
  complianceEnrollmentToUserPlanStatus,
  ComplianceUserPlanStatus,
} from '@/utils/migration';
import { withRoles } from '@/utils/withRoles';

import ReportsTeamMemberCompliancePlanCard from '@/components/Cards/PlanCard/PlanCards/ReportsTeamMemberCompliancePlanCard';
import ReportsTeamMemberPlanCard from '@/components/Cards/PlanCard/PlanCards/ReportsTeamMemberPlanCard';
import Filter, {
  FilterCheckboxSectionProps,
  FilterDesktop,
  FilterMobile,
} from '@/components/Filter';
import { FilterSearchBarProps } from '@/components/Filter/components/FilterSearchBar';
import { Header } from '@/components/header';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import LoadMorePaginator from '@/components/LoadMorePaginator/LoadMorePaginator';
import NoMessage from '@/components/NoMessage/NoMessage';
import { printTeamMember } from '@/components/print-report';
import {
  PrintCertificateModals,
  usePrintCertificates,
} from '@/components/PrintCertificates';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';

import Constants, { routes } from '@/constants';

import { reportTeamMemberOptions } from '@/_generated/api-clients/content';
import { UserPlanStatus } from '@/_generated/api-clients/xp';
import {
  DataViewAtomFamilyKey,
  sourceDataFamily,
  useDataView,
} from '@/atoms/data-view';
import { filtersToFilterInValues } from '@/atoms/data-view/helpers';

const toDataViewItem = (data: ComplianceUserPlanStatus) => {
  const isCompliance = data.planType === 'COMPLIANCE';
  const filterableStatus = getFilterableStatus(data.planStatus, isCompliance);
  const category = data.category;
  const filterableCategory =
    category === 'default'
      ? 'general'
      : category || Constants.PLAN_CATEGORIES.COMPLIANCE;
  const searchableName = itemByLanguage(data.planName, '').toLowerCase();
  const sortableCreated = new Date(data.planCreated ?? 0).getTime();
  const sortableDueDate = new Date(data.dueDate ?? 0).getTime();

  return {
    ...data,
    filterableCategory,
    filterableStatus,
    searchableName,
    sortableCreated,
    sortableDueDate,
  };
};

type ReportsTeamMembersDataViewItem = ReturnType<typeof toDataViewItem>;

const DATAVIEW_KEY = DataViewAtomFamilyKey.REPORTS_TEAM_MEMBERS_SUMMARY;
const sourceDataAtom = sourceDataFamily(DATAVIEW_KEY);

const PlansView = () => {
  const { t } = useTranslation();
  const { teamMemberId } = useParams<{ teamMemberId: string }>();
  const safeTeamMemberId = teamMemberId ?? '';
  const { xpCompliancePlans: inHouseCompliancePlansFF } = useFlags();
  const sortOptions = useSortOptions({ reportsTeamMembersView: true });
  const { data: auth } = useAuth();
  const isLicenseeUser = auth?.isLicensee ?? false;

  const setSourceData = useSetAtom(sourceDataAtom);

  const [
    {
      sort,
      filters,
      display: { items, showing, total },
    },
    { showMore, setSort, toggleCheckboxFilter, setSearch, resetSearch },
  ] = useDataView<ReportsTeamMembersDataViewItem>(DATAVIEW_KEY);

  const sortValue = sortOptions.find(
    so => so.direction === sort?.direction && so.key === sort?.key,
  );

  const filterAndSortButtonOptions = {
    onSortChange: setSort,
    sortOptions,
    sortValue,
    text: `${t('TrainingPlans.filtering.show')} ${
      showing
    } ${t('TrainingPlans.filtering.results')}`,
  };

  // Reports (Plans) by user (XP Proxy with compliance plans)
  const {
    data: oldReportsData,
    isSuccess: isSuccessOldReports,
    isFetching: isFetchingOldReport,
    isPending: isPendingOldReportRaw,
  } = useQuery({
    ...reportTeamMemberOptions({
      path: { adId: safeTeamMemberId },
    }),
    enabled: !!teamMemberId && !inHouseCompliancePlansFF,
    select: data =>
      inHouseCompliancePlansFF
        ? ({
            checklistResults: [],
            coursesResults: [],
            user: undefined,
          } as typeof data)
        : data,
  });

  const isPendingOldReport = inHouseCompliancePlansFF
    ? false
    : isPendingOldReportRaw;

  // New XP report plans by user (No compliance plans now)
  const { xpReportsData, isFetchingXpReport } =
    useGetXPSummaryReportByUser(teamMemberId);

  const user = xpReportsData?.user ?? oldReportsData?.user;

  const isFetchingReport =
    isFetchingOldReport || isPendingOldReport || isFetchingXpReport;

  const currentFilters = filtersToFilterInValues(filters);

  const categoryLabels = useCategoryLabelsGrouped(
    currentFilters,
    isLicenseeUser,
  );

  const statusLabels = {
    [Constants.TRAINING_PLANS.COMPLETED]: {
      translationString: t('TrainingPlans.statusOptions.completed'),
      value: currentFilters.includes(Constants.TRAINING_PLANS.COMPLETED),
    },
    [Constants.TRAINING_PLANS.NOT_COMPLETED]: {
      translationString: t('TrainingPlans.statusOptions.notCompleted'),
      value: currentFilters.includes(Constants.TRAINING_PLANS.NOT_COMPLETED),
    },
  };

  const searchHeaderOptions: FilterSearchBarProps = {
    onChange: e => setSearch(e.target.value, 'searchableName'),
    onClear: () => resetSearch(),
    placeholder: t('Reports.filtering.searchForPlans'),
    value: filters.search,
  };

  const categoriesOptions: FilterCheckboxSectionProps = {
    title: t('Browse.categories'),
    labels: categoryLabels,
    onToggle: v => toggleCheckboxFilter('filterableCategory', v),
  };

  const statusOptions: FilterCheckboxSectionProps = {
    title: t('Generic.status'),
    labels: statusLabels,
    onToggle: v => toggleCheckboxFilter('filterableStatus', v),
  };

  useEffect(() => {
    // start print user summary report once all data ready
    if (
      (!isSuccessOldReports && !inHouseCompliancePlansFF) ||
      !xpReportsData ||
      isFetchingOldReport ||
      isFetchingXpReport
    ) {
      return;
    }

    const plansData: UserPlanStatus[] = xpReportsData.planStatus;

    const complianceEnrollments =
      oldReportsData?.coursesResults?.[0]?.enrollments;
    const compliancePlans = complianceEnrollments
      ? Object.values(complianceEnrollments)
          .filter(enrollment => !enrollment.operatorDisabled)
          .map(complianceEnrollmentToUserPlanStatus)
      : [];

    const allPlans = isLicenseeUser
      ? plansData
      : [...plansData, ...compliancePlans];

    setSourceData(allPlans.map(toDataViewItem));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSuccessOldReports,
    isFetchingOldReport,
    isFetchingXpReport,
    oldReportsData,
    xpReportsData,
  ]);

  const filterOptions = {
    filterAndSortButtonOptions,
    search: <Filter.SearchBar {...searchHeaderOptions} />,
    children: (
      <>
        <Filter.CheckboxSection {...categoriesOptions} />
        <Filter.CheckboxSection {...statusOptions} />
      </>
    ),
  };

  useScrollMemory({
    container: 'body',
    key: DATAVIEW_KEY,
    enabled: !isFetchingReport,
  });

  const certificateHook = usePrintCertificates({ teamMemberId });

  return (
    <div className="relative flex max-w-full grow flex-col gap-4">
      <Header.Root>
        <Header.Back
          to={routes.reports.teamMembers}
          tx="Reports.tabTeamMembers"
        />
        <Header.Title>{user?.name ?? ''}</Header.Title>
        {certificateHook.shouldShow && (
          <Header.ActionIconButton
            data-testid="certificate-icon-button"
            icon={IconCertificate}
            onClick={certificateHook.handleClick}
            tooltip="Button.getCertificates"
            disabled={certificateHook.isLoading}
          />
        )}
        <Header.ActionIconButton
          data-testid="print-report-button"
          icon={IconPrinter}
          onClick={() =>
            printTeamMember({
              id: safeTeamMemberId,
              name: user?.name ?? '',
            })
          }
          tooltip="Button.printReport"
        />
      </Header.Root>
      <LoadingOverlay isOpen={isFetchingReport} />
      {!isFetchingReport && (
        <div className="centered-content md:mt-header-height-with-content-spacing px-4 max-md:mt-6">
          <FilterMobile {...filterOptions} />
          <SortFilterHeader
            className="md:pl-[304px]"
            label={t('TrainingPlans.filtering.sortBy')}
            onChange={setSort}
            onClear={() => {}}
            options={sortOptions}
            showClear={false}
            showCompletedPlansOption={false}
            showMyCompletedPlansOnlySwitchValue={false}
            text={`${total} ${t('Reports.tabPlans')}`}
            title={user?.name ?? ''}
            total={total}
            value={sortValue}
            mobileRightSideAction={
              certificateHook.shouldShow ? (
                <IconButton
                  className="p-0! md:hidden"
                  color="secondary"
                  onPress={certificateHook.handleClick}
                  data-testid="certificate-button-mobile"
                  isDisabled={certificateHook.isLoading}
                >
                  <IconCertificate width={24} height={24} />
                </IconButton>
              ) : (
                <IconButton
                  className="p-0! md:hidden"
                  color="secondary"
                  onPress={() =>
                    printTeamMember({
                      id: safeTeamMemberId,
                      name: user?.name ?? '',
                    })
                  }
                  data-testid="print-report-button"
                >
                  <IconPrinter width={24} height={24} />
                </IconButton>
              )
            }
          />
          <div className="relative flex max-w-full">
            <FilterDesktop {...filterOptions} />
            <div className="flex max-w-full grow flex-col overflow-hidden">
              <div className="relative flex max-w-full grow flex-col gap-4">
                {items.map(plan =>
                  plan.planType === 'COMPLIANCE' &&
                  !inHouseCompliancePlansFF ? (
                    <ReportsTeamMemberCompliancePlanCard
                      userPlanStatus={plan}
                      key={plan.planId}
                    />
                  ) : (
                    <ReportsTeamMemberPlanCard
                      key={plan.planId}
                      userPlanStatus={plan}
                      teamMemberId={user?.adId ?? ''}
                    />
                  ),
                )}
                {!items.length && !currentFilters.length && !filters.search && (
                  <NoMessage message={t('TrainingPlans.noTrainingPlans')} />
                )}
                {(!!currentFilters.length || !!filters.search) && !total && (
                  <NoMessage message={t('TrainingPlans.noPlansResults')} />
                )}
              </div>
            </div>
          </div>
          <LoadMorePaginator
            onClick={showMore}
            showing={showing}
            showingText={t('TrainingPlans.showingXOfYPlans', {
              showing,
              total: total,
            })}
            total={total}
          />
        </div>
      )}

      <PrintCertificateModals {...certificateHook} />
    </div>
  );
};

const PlansViewWithRoles = withRoles(PlansView, 'reporting.teamMember');

export default PlansViewWithRoles;
